<template>
  <ModalOverlay
    id="full-review-modal"
    width="74rem"
    :title="title"
    handle-top-layer="show"
  >
    <template #body>
      <div
        class="grid-x full-review-modal--grid"
        :class="reviewsGridClasses"
        :style="containerStyles"
      >
        <div
          v-if="showReviewImg"
          class="cell small-12 large-7 large-margin-right-s full-review-modal--left-side"
        >
          <div
            v-if="!isLoading"
            ref="slidesContainer"
            class="slides-container"
          >
            <FullImage
              v-if="reviewPhotos.length === 1"
              :url="reviewPhotos[0].url"
              :caption="reviewPhotos[0].caption"
            />
            <Slideshow
              v-else
              ref="fullReviewSlideShow"
              :list="reviewPhotos"
              :initial-index="selectedIndex"
              autoplay-mode="manual"
              @slideshow-change="onSlideShowChange"
            >
              <template #item="slotProps">
                <FullImage
                  :url="slotProps.data.url"
                  :caption="slotProps.data.caption"
                />
              </template>
            </Slideshow>
          </div>
          <template v-else-if="isLoading">
            <Skeleton height="350px" />
            <div class="margin-top-xxs">
              <Skeleton />
            </div>
          </template>
        </div>
        <div
          v-if="isLoading || review"
          class="cell large-auto small-12 full-review-modal--right-side"
          :class="reviewsRightSideClasses"
        >
          <IndividualGenericReview
            v-if="!isLoading"
            :review="review"
            :is-full-review="true"
            :is-comment-form-shown="openCommentId === review.reviewId"
            @toggleComment="handleToggleComment"
          />
          <FullReviewSkeleton v-else-if="isLoading" />
        </div>
      </div>
    </template>
  </ModalOverlay>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { onKeyStroke, useElementSize } from '@vueuse/core';

import { ModalOverlay } from '@atomic-ui/modalOverlay';
import Slideshow from '@atomic-ui/slideshow';
import Skeleton from '@common-vue/atomic-ui/src/components/skeleton';
import IndividualGenericReview from '@components/reviews/IndividualGenericReview';
import FullReviewSkeleton from '@components/reviews/FullReviewSkeleton';
import FullImage from '@components/reviews/FullImage';

const props = defineProps({
  selectedIndex: {
    required: true,
    type: Number,
  },
  title: {
    default: 'Customer Images',
    type: String,
  },
  photos: {
    required: true,
    /** @type import('vue').PropType<(ReviewPhotosObject & { reviewIndex: number })[]> */
    type: Array,
  },
  isLoading: { type: Boolean, default: false },
  reviews: {
    required: true,
    /** @type import('vue').PropType<Review[]> */
    type: Object,
  },
});

const slidesContainer = ref(null);
const fullReviewSlideShow = ref(null);
const openCommentId = ref(null);
const reviewPhotos = computed(() => props.photos?.map((d) => d) || []);
const currentIndex = ref(props.selectedIndex);

/** @type {import('vue').ComputedRef<Review>}  */
const review = computed(() => {
  const reviewIndex = props.photos?.[currentIndex.value]?.reviewIndex || 0;

  return props.reviews?.[reviewIndex];
});

// TODO -> update slideshow prop to make width and height of slide show images the same and remove width related variables here

// reviews images exist -> true
const showReviewImg = computed(() => reviewPhotos.value.length > 0);

// sliders container (left-side) height and width
const { height: slidersHeight, width: slidersWidth } = useElementSize(slidesContainer);

const heightDefined = computed(() => !!(showReviewImg.value && slidersHeight.value));
const widthDefined = computed(() => !!(showReviewImg.value && slidersWidth.value));

const containerStyles = computed(() => ({
  // defines the height of the right-side
  '--review-modal-container-height': heightDefined.value ? `${slidersHeight.value}px` : null,
  // TODO: remove later once atomic-ui is updated
  // width of the left-side
  '--review-modal-slider-width': widthDefined.value ? `${slidersWidth.value}px` : null,
}));

/** reviews classes * */
// no images in modal -> class="no-img"
const reviewsGridClasses = computed(() => (!showReviewImg.value ? 'no-img' : ''));
// no images in modal -> class="margin-top-z"
// images in modal -> class="small-margin-top-xs large-margin-top-z"
const reviewsRightSideClasses = computed(() => (showReviewImg.value ? 'small-margin-top-xs large-margin-top-z' : 'margin-top-z'));

/**
 *
 * @param {number} newPage
 */
function onSlideShowChange(newPage) {
  currentIndex.value = newPage - 1; // Math.max(0, count % (reviewPhotos.value.length));
}

const handleToggleComment = (reviewId) => {
  if (openCommentId.value === reviewId) {
    openCommentId.value = null;
  } else {
    openCommentId.value = reviewId;
  }
};

watch(() => props.selectedIndex, (newVal) => {
  currentIndex.value = newVal;
});

if (reviewPhotos.value.length > 1) {
  onKeyStroke(['ArrowLeft'], () => {
    fullReviewSlideShow.value?.move(-1);
  });
  onKeyStroke(['ArrowRight'], () => {
    fullReviewSlideShow.value?.move(1);
  });
}
</script>

<style lang="scss">
/* Replace position to keep child's reference at main content, solves toggletip position problem */
#full-review-modal .v-overlay-content{
  position: static;

  @include breakpoint(medium up) {
    max-width: 80%;
  }
}
</style>

<style lang="scss" scoped>
// reviews grid images (left-side) and review (right-side) ONLY!!
.full-review-modal--grid:not(.no-img) {
  $container-height: var(--review-modal-container-height);
  $slider-width: var(--review-modal-slider-width);

  // images and slider
  .full-review-modal--left-side {
    // slider image container
    :deep(div.item-wrapper) {
      // image width === image height
      @if($slider-width != null) {
        height: #{$slider-width};
      } @else {
        height: initial;
      }

      // image auto adjusts
      img.full-image {
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
      }
    }
  }

  // reviews details
  .full-review-modal--right-side {
    // only on large and above
    @include breakpoint(large up) {
      // left-side height === right-side height -> prevents the modal from getting jumpy
      @if($container-height != null) {
        max-height: #{$container-height};
        overflow-y: scroll;
      } @else {
        max-height:initial;
        overflow-y: initial;
      }
    }
  }
}

/* make the width of the fit sliders at most 300px when no image is present */
.no-img :deep(div:has(> div.fitslider)) {
  width: 100%;
  @include breakpoint(large up) {
    max-width: 300px;
  }
}
</style>
